import { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../header/index";
import Footer from "../../footer/index";
import HeaderSubMenuPage from "./headerSubMenu.js";
import {
  addBodyClass,
  getCashiHHeight,
  setAuthorizationToken,
} from "../../common/utils";
import { Scrollbars } from "react-custom-scrollbars-2";
import { useAuthenticated, useGetIdentity } from "react-admin";
import { PublisherInterface } from "@chili-publish/publisher-interface";
import { refreshTokenGen } from "../../../inMemoryJwt.js";
import axios from "axios";
import { getApiKeyWithStorage } from "../../common/utils/chiliStorage/index";
import { debounce } from "lodash";
import { useParams, useNavigate } from "react-router-dom";

function PersonnalisationPage() {
  useAuthenticated();
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupContent, setPopupContent] = useState({
    title: "",
    message: "",
    confirmAction: null,
  });
  const [loading, setLoading] = useState(true);
  const [showViewBtn, setShowViewBtn] = useState(false);
  const [isBlocking, setIsBlocking] = useState(true);
  const [activeView, setActiveView] = useState("brochure");
  const { id: editionId } = useParams();  
  const proId = localStorage.getItem("projectId");
  const publisherRef = useRef(null); // Ref to store the publisher instance
  const navigate = useNavigate();

  // Redirect to order page if proId is defined and valid
  useEffect(() => {
    if (
      proId !== undefined &&
      proId !== null &&
      proId !== "" &&
      proId !== "undefined"
    ) {
      navigate(`/parts-and-service/${editionId}/vos-elements`);
    }
  }, [proId, editionId, navigate]);

  // Toggles the overlay
  const toggleOverlay = () => {
    setOverlayOpen((prev) => !prev);
  };

  const checkEmptyVariables = async () => {
    if (publisherRef.current) {
      try {
        let valid = true;
        const varCount = (await publisherRef.current.getObject("document.variables.count")) - 1;

        for (let i = varCount; i >= 0; i--) {
          const variable = await publisherRef.current.getObject(`document.variables[${i}]`);
          console.log(`Checking variable ${i}:`, variable);

          const isRequired = variable.required === "true";
          const isImage = variable.dataType === "image";

          if (!isRequired) continue;

          const isEmptyValue = !isImage && variable.value === "";
          const isEmptyImage = isImage && variable.imgXML === "";

          if (isEmptyValue || isEmptyImage) {
            valid = false;
            console.log(`Required variable is empty:`, variable.name);
          }
        }

        if (!valid) {
          setPopupContent({
            title: "Attention – Champs vides",
            message: `
              Certaines variables obligatoires n’ont pas été remplies.
              <br />
              Merci de <strong>compléter toutes les informations requises</strong> avant de poursuivre.`,
            confirmAction: () => {
              closePopup();
            },
            showCancelButton: false,
          });
          setPopupVisible(true);
        }

        return valid;
      } catch (error) {
        console.error("Error checking empty variables:", error.message);
        return false;
      }
    } else {
      console.error("Publisher instance is not initialized.");
      return false;
    }
  };

  const handleCommanderClick = async () => {
    const variablesValid = await checkEmptyVariables();
    if (!variablesValid) return;
    withCleanDocument(
      async () => {
        if (activeView === "brochure") {
          switchView("courrier");
        } else {
          setPopupContent({
            title: "Attention – Validation définitive",
            message: `
              En cliquant sur "Commander", vous validez définitivement la personnalisation de votre support.
              <br />
              <strong>Aucune modification ne sera possible après cette étape.</strong>
              <br />
              Merci de bien vérifier que tous les éléments sont corrects avant de poursuivre.`,
            confirmAction: () => {
              window.location.href = `/parts-and-service/${editionId}/vos-elements`;
            },
            showCancelButton: true,
          });
          setPopupVisible(true);
        }
      },
      `Votre document n’a pas été sauvegardé.
      <br />
      Pensez à <strong>enregistrer vos modifications</strong> avant de quitter ou de valider`
    );
  };

  const checkDocumentDirtyState = async () => {
    if (publisherRef.current) {
      try {
        console.log(publisherRef.current);
        const dirtyState = await publisherRef.current.getObject("document.isDirty");
        console.log("Document Dirty State:", dirtyState);
  
        if (dirtyState) {
          setPopupContent({
            title: "Attention – Document non sauvegardé",
            message: `
              Votre document n’a pas été sauvegardé.
              <br />
              Pensez à <strong>enregistrer vos modifications</strong> avant de quitter ou de valider`,
            confirmAction: () => {
              // console.log("User acknowledged unsaved changes.");
              closePopup();
              // handleCommanderClick(); // Proceed with the action
            },
            showCancelButton: false, // Show the Cancel button for unsaved changes
          });
          setPopupVisible(true);
        } else {
          handleCommanderClick(); // Proceed with the action if no unsaved changes
        }
      } catch (error) {
        console.error("Error checking document dirty state:", error.message);
      }
    } else {
      console.error("Publisher instance is not initialized.");
    }
  };

  const checkDocumentDirtyStateForSwitch = async (nextView) => {
    if (publisherRef.current) {
      try {
        const dirtyState = await publisherRef.current.getObject("document.isDirty");
        if (dirtyState) {
          setPopupContent({
            title: "Attention – Document non sauvegardé",
            message: `
              Votre document n’a pas été sauvegardé.<br />
              Merci d'<strong>enregistrer vos modifications</strong> avant de changer de vue.`,
            confirmAction: () => {
              closePopup();
            },
            showCancelButton: false,
          });
          setPopupVisible(true);
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.error("Error checking document dirty state:", error.message);
        return false;
      }
    }
    return false;
  };

  const saveDocument = async () => {
    if (publisherRef.current) {
      try {
        console.log(publisherRef.current);
        const dirtyState = await publisherRef.current.executeFunction("document", "Save");
        console.log("Document Dirty State:", dirtyState);
      } catch (error) {
        console.error("Error saving document:", error.message);
      }
    } else {
      console.error("Publisher instance is not initialized.");
    }
  };

  const closePopup = () => {
    setPopupVisible(false); // Hide the popup
  };

  const withCleanDocument = async (action, dirtyMessage, showCancelButton = false) => {
    if (publisherRef.current) {
      try {
        const dirtyState = await publisherRef.current.getObject("document.isDirty");
        if (dirtyState) {
          setPopupContent({
            title: "Attention – Document non sauvegardé",
            message: dirtyMessage ||
              `Votre document n’a pas été sauvegardé.<br />
              Merci d'<strong>enregistrer vos modifications</strong> avant de poursuivre.`,
            confirmAction: () => closePopup(),
            showCancelButton,
          });
          setPopupVisible(true);
          return false;
        } else {
          await action();
          return true;
        }
      } catch (error) {
        console.error("Error checking document dirty state:", error.message);
        return false;
      }
    }
    return false;
  };

  // Combined state objects for efficiency
  const [state, setState] = useState({
    eleEdition: "",
    winHeight: null,
    calcHeight: null,
    apiKey: null,
    error: null,
    doc: null,
  });

  useEffect(() => {
    // Handle window resizing with debounce
    const handleResize = debounce(() => {
      setState((prev) => ({
        ...prev,
        winHeight: window.innerHeight,
        calcHeight: getCashiHHeight(),
      }));
    }, 300);

    handleResize();
    addBodyClass();
    // Adding event listeners
    window.addEventListener("resize", handleResize);

    /* const handleBeforeUnload = (event) => {
      console.log(publisher);      
      event.preventDefault(); // Standard way to block navigation
      event.returnValue = ""; // Some browsers require this for the dialog to appear
      console.log("User is leaving the page!");
    };

    window.addEventListener("beforeunload", handleBeforeUnload); */


    return () => {
      // Cleanup event listeners
      window.removeEventListener("resize", handleResize);
      // window.removeEventListener("beforeunload", handleBeforeUnload);
    };

  }, []);

  useEffect(() => {
  const urlLocation = window.location.href.split("/").slice(-2, -1)[0];
  setState((prev) => ({ ...prev, eleEdition: urlLocation }));
  }, []);

  // Fetch API key if missing
  useEffect(() => {
    if (!state.apiKey) {
      fetchApiKey();
    }
  }, [state.apiKey]);

  // Fetch document when apiKey and eleEdition are present and doc is missing
  useEffect(() => {
    if (state.apiKey && state.eleEdition && !state.doc) {
      getDocument(state.eleEdition);
    }
  }, [state.apiKey, state.eleEdition, state.doc]);

  // Initialize editor when apiKey and doc are present
  useEffect(() => {
    if (state.apiKey && state.doc) {
      initializeEditor(state.doc?.docId, 'brochure');
    }
  }, [state.apiKey, state.doc]);

  const fetchApiKey = async () => {
    try {
      const storageKey = "chiliApiKey";
      const apiKey = await getApiKeyWithStorage(storageKey);
      setState((prev) => ({ ...prev, apiKey }));
    } catch (err) {
      setState((prev) => ({ ...prev, error: "Failed to fetch API key." }));
      console.error(err);
    }
  };

  const getDocument = async (edition) => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refresh_token");
    const userid = JSON.parse(localStorage.getItem("userDetails"))?.id;

    console.log("getDoc");

    try {
      const response = await apiCall(
        `${process.env.REACT_APP_API_URL}/chilipublish/getMyPerso/${edition}/user/${userid}`,
        { Authorization: `Bearer ${token}` }
      );
      console.log(response);

      setState((prev) => ({ ...prev, doc: response }));
    } catch (error) {
      console.error("Error fetching document:", error.message);
    }
  };

  useEffect(() => {
    if (activeView === "brochure") {
      initializeEditor(state.doc?.docId, "brochure");
    } else if (activeView === "courrier") {
      initializeEditor(state.doc?.courId, "courrier");
    }
  }, [activeView]); // Run this effect

  const switchView = async (view) => {
    if (activeView === view) return;
    const variablesValid = await checkEmptyVariables();
    if (!variablesValid) return;
    withCleanDocument(
      async () => {
        setLoading(true);
        setShowViewBtn(false);
        setActiveView(view);
      },
      `Votre document n’a pas été sauvegardé.<br />
      Merci d'<strong>enregistrer vos modifications</strong> avant de changer de vue.`
    );
  };

  const initializeEditor = async (documentId, view) => {
    if (!state.doc) {
      console.error("Document ID is not available.");
      return;
    }
    console.log("initEditor with view & id, ", view, documentId);

    // Dynamically select the correct editor-div based on the active view
    const editorDivId = view === "brochure" ? "editor-div-brochure" : "editor-div-courrier";
    const editorDiv = document.getElementById(editorDivId);
    const forumValidationCnt = document.getElementById("forumValidationCnt");

    // Dynamically select wsId and vpId based on the active view
    const currWsId = view === "brochure" ? state.doc?.wsId : state.doc?.cwsId;
    const currVpId = view === "brochure" ? state.doc?.vpId : state.doc?.cvpId;


    const editorUrl = `https://cp-bzg-082.chili-publish.online/cp-bzg-082/editor_html.aspx?doc=${documentId}&apiKey=${state.apiKey}&ws=${currWsId || ""}&vp=${currVpId || ""}`;
    console.log("editorUrl", editorUrl);

    try {
      const publisher = await PublisherInterface.buildOnElement(editorDiv, editorUrl);
      publisher.iframe.classList.add("chiliIframe");

      console.log(publisher);

      // Store the publisher instance in the ref
      publisherRef.current = publisher;

      await publisher.addListener("DocumentFullyRendered", () => {
        console.log("Document is fully rendered puck.");
        setShowViewBtn(true); // Set showViewBtn to true
      });

      // Automatically scroll to the editor div with a 20px offset
      const forumValidationCntPosition =
        forumValidationCnt.getBoundingClientRect().top + window.scrollY - 20; // Adds the offset
      window.scrollTo({ top: forumValidationCntPosition, behavior: "smooth" });

      // Disable scrolling on the parent window
      document.body.classList.add("no-scroll");

      setLoading(false); // Hide the loading spinner after the editor is initialized
    } catch (error) {
      console.error("Error initializing CHILI editor:", error.message);
    }
  };

  const apiCall = async (url, headers) => {
    try {
      const response = await axios.get(url, { headers });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("Access token expired. Refreshing...");

        const refreshSuccess = await refreshTokenGen(
          localStorage.getItem("refresh_token")
        );
        if (refreshSuccess) {
          const newToken = localStorage.getItem("token");
          return await axios.get(url, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
        } else {
          throw new Error("Unauthorized: Unable to refresh access token.");
        }
      }
      throw error;
    }
  };

  const handlePdfDownload = () =>
    withCleanDocument(
      async () => {
        const token = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refresh_token");
        const userid = JSON.parse(localStorage.getItem("userDetails"))?.id;
        const documentId = activeView === "brochure" ? state.doc?.docId : state.doc?.courId;
        try {
          setLoading(true);
          const pdfUrl = `${process.env.REACT_APP_API_URL}/chilipublish/getPdf/${documentId}/user/${userid}?quality=low`;
          const response = await axios.get(pdfUrl, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const pdfDownloadUrl = response.data;
          const link = document.createElement("a");
          link.href = pdfDownloadUrl;
          link.download = `previsualisation_${activeView}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log("PDF downloaded successfully!");
        } catch (error) {
          console.error("Error downloading PDF:", error.message);
          if (error.response?.status === 401) {
            console.log("Access token expired. Attempting refresh...");
            const refreshSuccess = await refreshTokenGen(refreshToken);
            if (refreshSuccess) {
              handlePdfDownload(); // Retry the download after refreshing the token
            } else {
              alert("Failed to refresh token. Please log in again.");
            }
          } else {
            alert("Failed to download the PDF. Please try again later.");
          }
        } finally {
          setLoading(false);
        }
      },
      `Votre document n’a pas été sauvegardé.<br />
      Merci d'<strong>enregistrer vos modifications</strong> avant de télécharger la prévisualisation.`
    );

  return (
    <>
      {/* Popup */}
      {isPopupVisible && (
        <div className="custom-popup-overlay">
          <div className="custom-popup">
            <h2 className="colorRed">{popupContent.title}</h2>
            <p dangerouslySetInnerHTML={{ __html: popupContent.message }}></p> {/* Render HTML */}
            <div
              className={`popup-buttons ${
                !popupContent.showCancelButton ? "justify-content-center" : ""
              }`}
            >
              <button onClick={popupContent.confirmAction} className="confirm-button">
                Confirmer
              </button>
              {/* Conditionally render the Cancel button */}
              {popupContent.showCancelButton && (
                <button onClick={closePopup} className="cancel-button">
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loader-overlay">
          <div className="loader-spinner"></div>
        </div>
      )}
      <Header />
      <div className="breadCrumbSec">
        <div className="container siteContainer">
          <div className="row">
            <div className="col-12">
              <ul
                className="resetList breadCrumbList"
                role="navigation"
                aria-label="Breadcrumb"
              >
                <li>
                  <a href="/parts-and-service" className="breadCrumbLink">
                    Parts & Service
                  </a>
                </li>
                <li>
                  <span>Personnalisation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="pageDataSec withoutsubHead personnalisationPage">
        <div className="container siteContainer">
          <div className="row">
            <div className="col-8 leftAlignWidth">
              <HeaderSubMenuPage edtionId={state.eleEdition} proId={proId}  checkDocumentDirtyState={checkDocumentDirtyState} />
            </div>
          </div>
          <div id="forumValidationCnt" className="forumValidationCnt">
          {!loading && showViewBtn && (
            <div
              style={{
                position: "absolute",
                right: "75px",
                top: "7px",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                zIndex: 2,
              }}
            >
              {/* Button 1: brochure */}
              <button
                onClick={() => switchView("brochure")}
                className={`state-button brochurebtn ${activeView === "brochure" ? "active" : ""}`}
              >
                <span>Brochure</span>
              </button>

              {/* Button 2: courrier */}
              <button
                onClick={() => switchView("courrier")}
                className={`state-button courrierbtn secondBtn ${activeView === "courrier" ? "active" : ""}`}
              >
                <span>Courrier</span>
              </button>
            </div>
            
          )}
            <div className="forumValidationCntInner contains-editor">
              <div className="row">
                <div className="col-12">
                  {activeView === "brochure" && (
                    <div id="editor-div-brochure" className="editor-div">
                      {/* Use state.doc for personnalisation data */}
                    </div>
                  )}
                  {activeView === "courrier" && (
                    <div id="editor-div-courrier" className="editor-div">
                      {/* Use state.courrier for courrier data */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Two Buttons on the Right Side */}
          {!loading && showViewBtn && (
            <div
              style={{
                position: "fixed",
                right: "0px",
                top: "20%",
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                zIndex: 1000,
              }}
            >
              {/* Button 1: Save */}
              <button onClick={saveDocument} className="icon-button save-btn">
                <i className="iconmoon icon-floppy-disk"></i>{" "}
                <span>Enregistrer</span>
              </button>

              {/* Button 3: Download Preview */}
              <button onClick={handlePdfDownload} className="icon-button download-btn">
                <i className="iconmoon icon-download"></i>{" "}
                {/* Icon (Font Awesome example) */}
                <span>Télécharger la prévisualisation</span>
              </button>

              {/* Button 4: Place Order */}
              <button onClick={checkDocumentDirtyState} className="icon-button order-btn">
                <i className="iconmoon icon-checkbox-checked"></i>{" "}
                <span>Commander</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PersonnalisationPage;
